import { type ChainAvailabilityItem } from '@/models/appointment-chain-availability.ts';
import { type BookCollectionRequest, bookCollectionRequestSchema } from '@/models/book-collection-request.ts';
import { type BookCollectionResponse, bookCollectionResponseSchema } from '@/models/book-collection-response.ts';
import { type UserSession } from '@/models/user-session.ts';

import { processAPIWithHeaders } from '@/utils/helpers.js';
import { formatInsuranceType } from '@/utils/insurance-utils.ts';

export type BookCollectionParams = {
    locationKey: string;
    chainConfigurationId: string;
    insurance: string | null | undefined;
    isReturningPatient: boolean;
    additionalInfo: string;
    availability: ChainAvailabilityItem[];
};

/**
 * Maps the held chain selection to the request body and validates it. ISO slot times are sent
 * verbatim because the BE works in UTC (browser-local times are display-only). Throws on an unknown
 * insurance or an invalid body.
 */
const buildRequestBody = ({
    locationKey,
    chainConfigurationId,
    insurance,
    isReturningPatient,
    additionalInfo,
    availability,
}: BookCollectionParams): BookCollectionRequest => {
    const insuranceType = formatInsuranceType(insurance);
    if (!insuranceType) {
        throw new Error('book-collection: unrecognised insurance type');
    }

    const parsed = bookCollectionRequestSchema.safeParse({
        location_key: locationKey,
        chain_configuration_id: chainConfigurationId,
        insurance_type: insuranceType,
        is_new: !isReturningPatient,
        ...(additionalInfo ? { info: additionalInfo } : {}),
        appointment_slots: availability.map(
            ({ step_order: stepOrder, calendar_id: calendarId, start_date: startDate, end_date: endDate }) => ({
                step_order: stepOrder,
                calendar_id: calendarId,
                start_date: startDate,
                end_date: endDate,
            })
        ),
    });

    if (!parsed.success) {
        console.warn('book-collection: invalid request:', parsed.error.issues);
        throw new Error('book-collection: invalid request');
    }

    return parsed.data;
};

/**
 * Books a chain appointment collection on the secure patient host, building the request body from
 * the held chain selection.
 *
 * `processAPIWithHeaders` returns `res.json()` even on 4xx/5xx, so an HTTP error or a body that
 * fails validation surfaces as a thrown Error rather than a silent partial result.
 */
export const bookCollection = async (
    usersession: UserSession | undefined,
    userId: string,
    customerId: string,
    params: BookCollectionParams
): Promise<BookCollectionResponse> => {
    const token = usersession?.idToken.jwtToken;
    if (!token) {
        throw new Error('book-collection: missing auth token');
    }

    const request = buildRequestBody(params);

    const response = await processAPIWithHeaders(`secure/user/appointments/book-collection?user_id=${userId}`, 'POST', {
        body: request,
        headers: { 'Content-Type': 'application/json', Authorization: token, customer_id: customerId },
    });

    const parsed = bookCollectionResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('book-collection: invalid response:', parsed.error.issues);
        throw new Error('book-collection: invalid response');
    }

    return parsed.data;
};
